/**
 * EstimatesSection — Section B of the restructured panel.
 *
 * Collapsed wrapper around all calculated / speculative values
 * (CAPEX, OPEX, LCOH, annual energy, fibre lease, plant capacity).
 * Every row is an EstimateRow that drops down its own methodology
 * on click.
 *
 * Audit 2026-05-27 (restructure): pulls these numbers out of the
 * top-level scan grid + BottomLine so they don't share visual
 * weight with measured facts in VerifiedSiteData.
 */

import React from 'react';
import EstimateRow from './EstimateRow';

const PLANT_CAPEX_MNOK_PER_MW = { data_center: 50, hydrogen: 10 };

export default function EstimatesSection({ site, useCase, projectMw, buildableDetail = null }) {
  if (!site) return null;

  const mw = Number(projectMw || 100);
  const utilization = useCase === 'data_center' ? 0.85 : 0.60;
  const hoursYr = 8760;
  const elec = site.electricity_price_nok_mwh;

  // ── Capacity estimate ─────────────────────────────────────────────
  const capacityRow = site.max_practical_mw != null && Array.isArray(site.capacity_steps) ? (
    <EstimateRow
      label="Site Capacity (IT load)"
      range={`~${site.max_practical_mw} MW`}
      assumptions={site.capacity_steps}
    />
  ) : null;

  // ── CAPEX components + total ──────────────────────────────────────
  // Water pipe rate aligned with lcoh_service (10 MNOK/km) 2026-05-28.
  // Was 3.5 here, 10 in the backend LCOH path — same site reported two
  // different water CAPEX numbers depending on which view you opened.
  const grid    = Number(site.grid_capex_mnok || 0);
  const upg     = Number(site.voltage_upgrade_mnok || 0);
  const waterKm = site.water_distance_km ?? site.nearest_water_distance_km;
  const water   = waterKm != null ? Number(waterKm) * 10 : 0;   // MNOK/km — matches lcoh_service
  const roadKm  = site.nearest_road_distance_km;
  const road    = roadKm != null ? Number(roadKm) * 20 : 0;
  const perMw   = PLANT_CAPEX_MNOK_PER_MW[useCase === 'data_center' ? 'data_center' : 'hydrogen'];
  const plant   = perMw * mw;
  const totalMid = grid + upg + water + road + plant;
  const fmtCapex = (mnok) => mnok >= 1000 ? `${(mnok/1000).toFixed(1)} B` : `${Math.round(mnok)} M`;

  const plantAssumption = useCase === 'data_center'
    ? `Plant equipment: ${Math.round(plant)} MNOK (${perMw} MNOK/MW × ${mw} MW · enterprise-tier shell + M&E; hyperscale would be lower)`
    : `Plant equipment: ${Math.round(plant)} MNOK (${perMw} MNOK/MW × ${mw} MW · electrolyser + BoP, indicative)`;

  const capexRow = totalMid > 0 ? (
    <EstimateRow
      label="Indicative CAPEX"
      range={`${fmtCapex(totalMid * 0.75)}–${fmtCapex(totalMid * 1.25)} NOK`}
      assumptions={[
        plantAssumption,
        grid > 0 ? `Grid connection: ${Math.round(grid)} MNOK (lcoh_service rate × distance)` : null,
        upg > 0  ? `Voltage upgrade: ${Math.round(upg)} MNOK (VOLTAGE_UPGRADE_MNOK lookup)` : null,
        water > 0 ? `Water pipe: ${Math.round(water)} MNOK (10 MNOK/km × ${waterKm.toFixed(1)} km · matches LCOH)` : null,
        road > 0  ? `Access road: ${Math.round(road)} MNOK (20 MNOK/km × ${roadKm.toFixed(1)} km)` : null,
        '±25% uncertainty band — early-stage indicative only',
        'Excludes: land cost, environmental permits, working capital, IT equipment, fit-out',
      ].filter(Boolean)}
    />
  ) : null;

  // ── Annual energy cost ────────────────────────────────────────────
  const annualEnergyMnok = elec != null && mw > 0
    ? (mw * utilization * hoursYr * elec) / 1_000_000
    : null;
  const energyRow = annualEnergyMnok != null ? (
    <EstimateRow
      label="Annual Energy Cost"
      range={`~${annualEnergyMnok.toFixed(0)} MNOK/yr`}
      assumptions={[
        `${mw} MW load × ${(utilization*100).toFixed(0)}% utilisation × ${hoursYr} h/yr`,
        `Price: ${Math.round(elec/10)} øre/kWh (${site.electricity_price_source || 'unknown source'})`,
        useCase === 'data_center'
          ? 'PUE assumed 1.0 (IT only) — total energy ≈ load × PUE × utilisation'
          : 'Electrolyser stack only — excludes BoP and compression',
        'Excludes: grid tariff, network charges, surcharges, taxes',
      ]}
    />
  ) : null;

  // ── Free-cooling savings (DC only) ────────────────────────────────
  const days = site.free_cooling_days_yr;
  let coolingRow = null;
  if (useCase === 'data_center' && days != null && elec != null && mw > 0) {
    const freeFrac = Math.min(1, days / 365);
    const pueDelta = 0.30 * freeFrac;
    const savingsMnok = (mw * pueDelta * hoursYr * elec) / 1_000_000;
    coolingRow = (
      <EstimateRow
        label="Free-Cooling Savings"
        range={`~${savingsMnok.toFixed(0)} MNOK/yr`}
        assumptions={[
          `Baseline PUE 1.50 (mechanical-only) vs achievable PUE ${(1.50 - pueDelta).toFixed(2)}`,
          `${days} free-cool days/yr → free-cool fraction ${freeFrac.toFixed(2)}`,
          `PUE delta × ${mw} MW × ${hoursYr} h × power price`,
          'Real PUE depends on design (containment, water-side economiser, etc.)',
        ]}
      />
    );
  }

  // Fibre lease estimate removed 2026-05-28 — 2× spread (4–8 kNOK/km/yr)
  // is speculative; carrier negotiation, contract length, and on-net vs
  // off-net all swamp the model. Doesn't affect site choice. The Fibre
  // meet-point row in Compare carries the relevant identity / distance
  // / carrier-count info; lease cost can come from a real carrier quote.

  // ── Grading cost (DC only, from buildable-detail Stage 4) ─────────
  // Only renders after the user clicks "Compute precise buildable area"
  // and the /sites/buildable-detail endpoint returns a cut/fill volume.
  // Range = cut_fill × [140, 420] NOK/m³ (NOK-2026 earthworks band:
  // low = soft ground, short haul, simple grading; high = rock, blasting,
  // long haul, complex grading). Point estimate uses the midpoint.
  let gradingRow = null;
  if (useCase === 'data_center' && buildableDetail
      && buildableDetail.grading_cost_range_mnok
      && (buildableDetail.cut_fill_volume_m3 || 0) > 0) {
    const [lo, hi] = buildableDetail.grading_cost_range_mnok;
    const mid      = buildableDetail.grading_cost_point_mnok;
    const m3       = buildableDetail.cut_fill_volume_m3;
    const slopeDist = buildableDetail.slope_distribution || {};
    const flatGentlePct = Math.round(((slopeDist.flat || 0) + (slopeDist.gentle || 0)) * 100);
    gradingRow = (
      <EstimateRow
        label="Grading Cost"
        range={`${lo}–${hi} MNOK (point ~${mid})`}
        assumptions={[
          `Cut/fill volume: ${m3.toLocaleString()} m³ (median-elevation target across buildable cells)`,
          `Unit cost band: 140–420 NOK/m³ — Norwegian earthworks (2026)`,
          `Low end: soft ground, short haul, simple grading`,
          `High end: rock, blasting, long haul, complex grading`,
          `Slope distribution: ${flatGentlePct}% of cells flat-to-gentle (≤15%); rest needs cut/fill`,
          'Site-specific bedrock + access conditions can shift this significantly.',
          'For a binding number: civil-contractor quote against the precise buildable polygon.',
        ]}
      />
    );
  }

  const rows = [capacityRow, capexRow, energyRow, coolingRow, gradingRow].filter(Boolean);
  if (rows.length === 0) return null;

  return (
    <div className="analysis-section">
      <div className="analysis-section-header">
        Estimates · Click each row for methodology
      </div>
      <div style={{ paddingLeft: 8 }}>
        {rows}
      </div>
    </div>
  );
}
